import React, { useState } from 'react';
import { Icons } from '../lib/Icons';

const InterventionsPlanDisplay = ({ plan, onAcceptIntervention, onAcceptGroup, onRejectSuggestion }) => {
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    interventions: true,
    existingGroups: false,
    newGroups: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getNcStatusLabel = (status) => {
    const labels = {
      'at_nc': 'At or near National Curriculum expectations',
      'nc_below12': 'On National Curriculum but 1-2 years below',
      'below_nc': 'Working below the National Curriculum',
      'not_nc': 'Not following National Curriculum (highly personalised)'
    };
    return labels[status] || status;
  };

  const getAbilityLevelLabel = (level) => {
    const labels = {
      'full-sentences': 'Full sentences',
      'short-phrases': 'Short phrases',
      'single-words': 'Single words',
      'non-verbal-no-initiation': 'Non-verbal, no initiation',
      'multi-step': 'Multi-step instructions',
      'one-to-two-step': '1-2 step instructions',
      'single-step': 'Single step instructions',
      'minimal-understanding': 'Minimal understanding',
      'emerging-abstract': 'Emerging abstract thinking',
      'needs-modelling': 'Needs modelling',
      'concrete-only': 'Concrete only',
      'cause-effect': 'Cause and effect',
      'self-regulates': 'Self-regulates',
      'co-regulation': 'Co-regulation',
      'frequent-support': 'Frequent support',
      'constant-support': 'Constant support',
      'manages-well': 'Manages well',
      'needs-preparation': 'Needs preparation',
      'struggles': 'Struggles',
      'distressed': 'Becomes distressed',
      'participates-confidently': 'Participates confidently',
      'participates-with-support': 'Participates with support',
      'reluctant': 'Reluctant',
      'avoids': 'Avoids groups'
    };
    return labels[level] || level;
  };

  const getInterventionTypeIcon = (type) => {
    return type === 'group' ? Icons.Users : Icons.User;
  };

  const getFrequencyColor = (frequency) => {
    const colors = {
      'daily': 'bg-red-100 text-red-800',
      '2-3x/week': 'bg-orange-100 text-orange-800',
      'weekly': 'bg-blue-100 text-blue-800'
    };
    return colors[frequency] || 'bg-gray-100 text-gray-800';
  };

  const getMatchScoreColor = (score) => {
    if (score >= 20) return 'bg-green-100 text-green-800';
    if (score >= 15) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (!plan) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="text-center text-gray-500">
          <p>No interventions plan available. Complete the questionnaire to generate recommendations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Child Profile Echo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('profile')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
        >
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Icons.ClipboardDocumentList className="w-5 h-5 text-indigo-600" />
            Child Profile Summary
          </h3>
          <svg
            className={`w-5 h-5 text-gray-400 transform transition-transform ${
              expandedSections.profile ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.profile && (
          <div className="px-6 pb-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Basic Information</h4>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Name:</span> {plan.childProfileEcho.name}</div>
                  <div><span className="font-medium">Year Group:</span> {plan.childProfileEcho.yearGroup}</div>
                  <div><span className="font-medium">NC Status:</span> {getNcStatusLabel(plan.childProfileEcho.ncStatus)}</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Ability Profile</h4>
                <div className="space-y-2 text-sm">
                  {plan.childProfileEcho.abilityProfile.expressive_level && (
                    <div><span className="font-medium">Expressive:</span> {getAbilityLevelLabel(plan.childProfileEcho.abilityProfile.expressive_level)}</div>
                  )}
                  {plan.childProfileEcho.abilityProfile.receptive_level && (
                    <div><span className="font-medium">Receptive:</span> {getAbilityLevelLabel(plan.childProfileEcho.abilityProfile.receptive_level)}</div>
                  )}
                  {plan.childProfileEcho.abilityProfile.cognition_level && (
                    <div><span className="font-medium">Cognition:</span> {getAbilityLevelLabel(plan.childProfileEcho.abilityProfile.cognition_level)}</div>
                  )}
                  {plan.childProfileEcho.abilityProfile.regulation_independence && (
                    <div><span className="font-medium">Regulation:</span> {getAbilityLevelLabel(plan.childProfileEcho.abilityProfile.regulation_independence)}</div>
                  )}
                </div>
              </div>
            </div>
            
            {plan.childProfileEcho.needAreas && plan.childProfileEcho.needAreas.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Areas of Need</h4>
                <div className="flex flex-wrap gap-2">
                  {plan.childProfileEcho.needAreas.map((area, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommended Interventions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => toggleSection('interventions')}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
        >
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Icons.Target className="w-5 h-5 text-indigo-600" />
            Recommended Interventions ({plan.recommendedInterventions?.length || 0})
          </h3>
          <svg
            className={`w-5 h-5 text-gray-400 transform transition-transform ${
              expandedSections.interventions ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.interventions && (
          <div className="px-6 pb-6 border-t border-gray-200">
            {plan.recommendedInterventions && plan.recommendedInterventions.length > 0 ? (
              <div className="space-y-4 mt-4">
                {plan.recommendedInterventions.map((intervention, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{getInterventionTypeIcon(intervention.type)}</span>
                          <h4 className="font-medium text-gray-900">{intervention.title}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getFrequencyColor(intervention.recommendedFrequency)}`}>
                            {intervention.recommendedFrequency}
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-3">
                          {intervention.slotMins} minutes per session
                        </div>
                        
                        {intervention.focusTags && intervention.focusTags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {intervention.focusTags.map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {intervention.notesForStaff && intervention.notesForStaff.length > 0 && (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Staff Notes:</span>
                            <ul className="list-disc list-inside mt-1">
                              {intervention.notesForStaff.map((note, noteIndex) => (
                                <li key={noteIndex}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => onAcceptIntervention && onAcceptIntervention(intervention)}
                          className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => onRejectSuggestion && onRejectSuggestion('intervention', intervention.id)}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No interventions recommended based on current profile.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Existing Group Matches */}
      {plan.existingGroupMatches && plan.existingGroupMatches.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={() => toggleSection('existingGroups')}
            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
          >
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Icons.School className="w-5 h-5 text-indigo-600" />
              Existing Groups You Can Join ({plan.existingGroupMatches.length})
            </h3>
            <svg
              className={`w-5 h-5 text-gray-400 transform transition-transform ${
                expandedSections.existingGroups ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.existingGroups && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="space-y-4 mt-4">
                {plan.existingGroupMatches.map((match, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-gray-900">Group {match.groupId}</h4>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMatchScoreColor(match.matchScore)}`}>
                            Match Score: {match.matchScore}
                          </span>
                        </div>
                        
                        {match.reasons && match.reasons.length > 0 && (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Why this matches:</span>
                            <ul className="list-disc list-inside mt-1">
                              {match.reasons.map((reason, reasonIndex) => (
                                <li key={reasonIndex}>{reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => onAcceptGroup && onAcceptGroup(match)}
                          className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          Join Group
                        </button>
                        <button
                          onClick={() => onRejectSuggestion && onRejectSuggestion('group', match.groupId)}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Group Suggestions */}
      {plan.newGroupSuggestions && plan.newGroupSuggestions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={() => toggleSection('newGroups')}
            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              <Icons.Plus className="w-5 h-5 mr-2 inline-block" /> New Groups To Create ({plan.newGroupSuggestions.length})
            </h3>
            <svg
              className={`w-5 h-5 text-gray-400 transform transition-transform ${
                expandedSections.newGroups ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.newGroups && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="space-y-4 mt-4">
                {plan.newGroupSuggestions.map((suggestion, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-2">{suggestion.suggestedTitle}</h4>
                        
                        <div className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Capacity:</span> {suggestion.suggestedCapacity} students
                        </div>
                        
                        {suggestion.suggestedSchedule && suggestion.suggestedSchedule.length > 0 && (
                          <div className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">Schedule:</span> {suggestion.suggestedSchedule.map(s => `${s.day} ${s.time}`).join(', ')}
                          </div>
                        )}
                        
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Rationale:</span> {suggestion.rationale}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => onAcceptGroup && onAcceptGroup(suggestion)}
                          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          Create Group
                        </button>
                        <button
                          onClick={() => onRejectSuggestion && onRejectSuggestion('newGroup', suggestion.interventionId)}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InterventionsPlanDisplay;
